import * as Sentry from "@sentry/react";

import { isRecoveredChunkPreloadError } from "@/helpers/chunk-preload-error";

import { getBenignAuthErrorCode } from "./benign-auth-errors";
import { matchSubgraphFetchFailure } from "./subgraph-errors";
import { scrubSensitiveQueryParams } from "./utils";

const STALE_CHUNK_MESSAGE = "Failed to fetch dynamically imported module";

/**
 * A dispose race inside `@otjs/firebase-monaco`: it queues
 * `setTimeout(() => this._trigger(Ready))`, and `useFireMonaco`'s effect cleanup
 * disposes the adapter — nulling its emitter — before that timer runs. The editor
 * is already unmounted, so nothing the user can see is broken, and the library
 * exposes no way to cancel the pending trigger.
 *
 * Narrowed twice over, because "an emit failed inside @otjs" on its own also
 * describes a defect worth fixing. The frame has to be @otjs's, and the message
 * has to be a read off a nulled emitter — so `emit is not a function`, or any
 * error the adapter raises while it is alive, still reports. Wordings differ per
 * engine ("reading 'emit'" in V8, "this._emitter is null" in SpiderMonkey).
 */
function isDisposedEditorAdapterEmit(event: Sentry.ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception === undefined) {
    return false;
  }
  const value = exception.value ?? "";
  if (!/\bemit\b|_emitter/.test(value) || !/null|undefined/.test(value)) {
    return false;
  }
  return (exception.stacktrace?.frames ?? []).some((frame) =>
    frame.filename?.includes("@otjs/"),
  );
}

/**
 * Engine wordings for "the connection went away mid-request".
 */
const NETWORK_DROPOUT_MESSAGES = new Set([
  "Failed to fetch",
  "Load failed",
  "NetworkError when attempting to fetch resource.",
]);

/**
 * A connection that dropped mid-request, overwhelmingly on mobile Safari.
 *
 * The same message is what a real outage, a CORS misconfiguration or a CSP block
 * produces, so this takes only the shape that carries no information: an
 * unhandled rejection with no frames at all, naming neither a call site nor a
 * URL. `useWeldAPI` and the OAuth callback both await bare `fetch` calls, and a
 * genuine failure there arrives with a stack — that still reports, as does
 * anything we caught deliberately.
 */
function isStacklessNetworkDropout(event: Sentry.ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.mechanism?.handled !== false) {
    return false;
  }
  if ((exception.stacktrace?.frames ?? []).length > 0) {
    return false;
  }
  return NETWORK_DROPOUT_MESSAGES.has(exception.value ?? "");
}

/**
 * The LaunchDarkly SDK's own flag fetch, rejected with nobody awaiting it. The
 * distribution says client environment rather than a broken setup: 21 users
 * across 21 unrelated accounts over 90 days, a long tail of one or two events
 * each, and `LDFlagFetchError("network error")` is an outright failed XHR — a
 * blocker, a restrictive network, or an offline tab. A wrong client-side id or
 * environment would fail for every session and arrive as
 * LaunchDarklyUnexpectedResponseError with a status instead.
 *
 * Narrow on purpose. `WeldFeatureFlagProvider` catches and reports its own
 * `identify` failure, because that one leaves flag targeting silently wrong for
 * the session, and it can carry the same error name — so the unhandled mechanism
 * and the SDK frame are both part of the match.
 */
function isUnawaitedLaunchDarklyFetch(event: Sentry.ErrorEvent): boolean {
  const exception = event.exception?.values?.[0];
  if (exception?.mechanism?.handled !== false) {
    return false;
  }
  if (!exception.type?.startsWith("LaunchDarkly")) {
    return false;
  }
  return (exception.stacktrace?.frames ?? []).some((frame) =>
    frame.filename?.includes("launchdarkly-js-sdk"),
  );
}

export default function init(releaseId: string, environment: string) {
  Sentry.init({
    dsn: "https://7631783c3f414caa9817bab45bd7754c@o510832.ingest.sentry.io/5870611",
    integrations: [Sentry.browserTracingIntegration()],
    release: releaseId,
    environment,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    ignoreErrors: [
      // A browser scheduling artefact with no user-visible effect and no call
      // site to act on. Both wordings occur, Chrome's and Firefox's.
      /ResizeObserver loop/,
    ],
    denyUrls: [
      // Stonly's in-app guide widget, whose XHR parse failures are unactionable
      // from here.
      /\.stonly\.js/,
    ],
    beforeSend(event, hint) {
      // A stale chunk that we are already reloading past is not something anyone
      // can act on: the rejection is left uncancelled on purpose, so it arrives
      // here even though the user recovers. The case that cannot be recovered is
      // reported separately, with `wasForceReloaded` context.
      //
      // Scoped to exceptions so deliberate telemetry about the same chunk — a
      // message or transaction naming the URL — cannot suppress itself.
      if (
        event.exception !== undefined &&
        isRecoveredChunkPreloadError(event.exception.values?.[0]?.value)
      ) {
        return null;
      }
      // Applied centrally rather than at each call site: an expired session is
      // benign wherever it surfaces, and every place that awaits a token would
      // otherwise need the same guard.
      //
      // Grouped and downgraded rather than dropped. One of these is noise, but a
      // sustained rise in them is the only signal an auth regression gives us —
      // an errorLink redirect that stops working leaves every user looping
      // through exactly this error. One warning-level issue keeps that rate
      // alertable at the cost of one line in the issue list, instead of the eight
      // error-level groups it used to open.
      const benignAuthCode = getBenignAuthErrorCode(hint.originalException);
      if (benignAuthCode !== undefined) {
        event.fingerprint = ["expired-session"];
        event.level = "warning";
        // Breaks the one issue down by cause, so a regression that shifts the mix
        // (all invalid_grant, say) is visible inside it.
        event.tags = { ...event.tags, auth_error_code: benignAuthCode };
      }
      if (isDisposedEditorAdapterEmit(event)) {
        return null;
      }
      if (isUnawaitedLaunchDarklyFetch(event)) {
        return null;
      }
      if (isStacklessNetworkDropout(event)) {
        return null;
      }
      // Every deploy renames the chunks, so the message is unique per asset and
      // Sentry opens a fresh issue for each one. Group them by cause instead.
      if (
        event.exception?.values?.[0]?.value?.startsWith(STALE_CHUNK_MESSAGE)
      ) {
        event.fingerprint = [STALE_CHUNK_MESSAGE];
      }
      // The catching component lands in the culprit, so one hop opens an issue
      // per route. Keyed by subgraph and status, not cause alone, so a subgraph
      // in a genuine 500 crash loop stays alertable apart from the 502s.
      const subgraphFailure = matchSubgraphFetchFailure(
        event.exception?.values?.[0]?.value,
      );
      if (subgraphFailure !== undefined) {
        event.fingerprint = [
          "subgraph-fetch-error",
          subgraphFailure.subgraph,
          subgraphFailure.status,
        ];
        event.tags = {
          ...event.tags,
          subgraph: subgraphFailure.subgraph,
          subgraph_status: subgraphFailure.status,
        };
      }
      // Defense-in-depth: keep single-use tokens (e.g. the GitHub install choice
      // token in `?githubInstallState=`) out of captured request URLs. The same
      // scrub runs over navigation breadcrumbs below.
      if (event.request?.url) {
        event.request.url = scrubSensitiveQueryParams(event.request.url);
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (typeof breadcrumb.data?.from === "string") {
        breadcrumb.data.from = scrubSensitiveQueryParams(breadcrumb.data.from);
      }
      if (typeof breadcrumb.data?.to === "string") {
        breadcrumb.data.to = scrubSensitiveQueryParams(breadcrumb.data.to);
      }
      if (typeof breadcrumb.data?.url === "string") {
        breadcrumb.data.url = scrubSensitiveQueryParams(breadcrumb.data.url);
      }
      return breadcrumb;
    },
  });
}
